/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import axios, { AxiosRequestConfig, AxiosError, AxiosInstance } from 'axios';
import { stringify } from 'query-string';

import { Auth } from '../utils/auth';
import { asyncStorage } from './async-storage';
import { IAsyncStorage, IAuth } from '../typings';
import { amsDevicePingPath, API_BASE_URL, MAX_TIMEOUT_MS } from './constants';
import {
  convertToCamelCase,
  createDeviceAuthHeader,
  createPath,
  getDeviceCredentials,
  getErrorMessage,
  removeDeviceCredentials,
  removeDeviceIdFromStorage,
} from '../utils';
import { refreshAccessTokenIfNeeded } from './auth';
import { isValidURL } from './common';
interface Config {
  apiBaseURL?: string;
  siteVersion?: string;
  siteId?: string;
  onLogout?: (message?: 'MAX_AUTH_ERROR_THRESHOLD_REACHED') => void;
  storage?: IAsyncStorage;
  deviceId?: string | (() => Promise<string> | 'UNKNOWN') | (() => string);
  deviceIdGenerator: () => Promise<string>;
}

class Instance {
  public client: AxiosInstance;
  public storage: IAsyncStorage;
  public auth: IAuth;
  public config: Required<Pick<Config, 'onLogout' | 'deviceIdGenerator'>>;

  public constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      paramsSerializer(params: any): any {
        return stringify(params);
      },
    });
    this.storage = asyncStorage;
    this.auth = new Auth(this.storage);
    this.config = {
      onLogout: async () => {
        await this.auth.removeAccessToken();
        await this.auth.removeRefreshToken();
        await removeDeviceCredentials();
        await removeDeviceIdFromStorage();
      },
      deviceIdGenerator: async () => '',
    };
  }

  public setStorage(storage: IAsyncStorage) {
    this.storage = storage;
    this.auth = new Auth(this.storage);
  }
}

export const instance = new Instance();

let refreshTokenTimeouReference: number | null = null;
export const startAccessTokenRefreshProcess = async (
  force = false,
  graceMinutes = 1
) => {
  // clear any previous timeout
  if (refreshTokenTimeouReference) {
    window.clearTimeout(refreshTokenTimeouReference);
    refreshTokenTimeouReference = null;
  }

  const { expiresInMS, graceMs } = await refreshAccessTokenIfNeeded(
    force,
    graceMinutes
  );

  // set timeout to refresh token half a minute before it's expiry
  let timeoutDelay = Math.max(expiresInMS - graceMs, 30000); // timeout interval not less than 30 seconds
  if (timeoutDelay > MAX_TIMEOUT_MS) {
    timeoutDelay = MAX_TIMEOUT_MS;
  }

  refreshTokenTimeouReference = window.setTimeout(
    () =>
      startAccessTokenRefreshProcess(true).catch((e) => {
        console.error(e);
      }),
    timeoutDelay
  );
};

// axios middleware
instance.client.interceptors.request.use(
  // @ts-ignore
  async (
    requestConfig: AxiosRequestConfig & { noAuth?: boolean }
  ): Promise<AxiosRequestConfig> => {
    // @ts-ignore
    (requestConfig as any)._startTime = Date.now();
    // @ts-ignore
    (requestConfig as any)._requestId = Math.random().toString(36).slice(2, 8);
    console.log(
      `[API REQUEST:${
        (requestConfig as any)._requestId
      }] ${new Date().toLocaleTimeString()} ${requestConfig.method?.toUpperCase()} ${
        requestConfig.url
      }`
    );
    const updatedConfig = requestConfig;
    try {
      const token = await instance.auth.getAccessToken();
      if (token && !updatedConfig.noAuth) {
        // @ts-ignore
        updatedConfig.headers.Authorization = `bearer ${token}`;
      }
    } catch (error) {
      console.error({ error });
    }

    const queryString = stringify(requestConfig.params) || '';

    const url = `${requestConfig.baseURL}${requestConfig.url}${
      queryString && `?${queryString}`
    }`;

    try {
      const deviceCreds = await getDeviceCredentials();
      if (deviceCreds && !updatedConfig.noAuth) {
        const authHeader = await createDeviceAuthHeader({
          url,
          method: requestConfig.method?.toUpperCase() as 'GET',
          headers: requestConfig.headers,
          data: requestConfig.data,
        });
        // @ts-ignore
        updatedConfig.headers.Authorization = authHeader;
      }
    } catch (error) {
      console.error({ error });
    }

    try {
      const deviceId = await instance.config.deviceIdGenerator();
      if (deviceId) {
        // @ts-ignore
        updatedConfig.headers['x-device-id'] = deviceId;
      }
    } catch (error) {
      console.error({ error });
    }

    return {
      ...updatedConfig,
    };
  },
  (error: any): Promise<AxiosError> => Promise.reject(error)
);

instance.client.interceptors.response.use(
  (res: any): any => {
    const reqId = (res.config as any)?._requestId || '?';
    const startedAt = (res.config as any)?._startTime || Date.now();
    console.log(
      `[API RESPONSE:${reqId}] ${new Date().toLocaleTimeString()} ${res.config?.method?.toUpperCase()} ${
        res.config?.url
      } -> ${res.status} (${Date.now() - startedAt}ms)`
    );
    const data = res.data.data || res.data;
    return res.config.convertResponseToCamelCase !== false
      ? convertToCamelCase(data)
      : data;
  },
  async (error: any): Promise<any> => {
    const reqId = error?.config?._requestId || '?';
    const startedAt = error?.config?._startTime || Date.now();
    console.error(
      `[API ERROR:${reqId}] ${new Date().toLocaleTimeString()} ${error?.config?.method?.toUpperCase()} ${
        error?.config?.url
      } -> ${error?.response?.status || 'NO_RESPONSE'} (${
        Date.now() - startedAt
      }ms)`
    );
    console.error(error);
    const response = error.response || {};
    const { data = {} } = response;
    let errorMessage = 'Something went wrong. Please try again later.';
    // few APIs which do not require authentication also send 401
    // for eg. forgot-password sends 401 when email is unverified
    switch (response.status) {
      case undefined:
        if (!navigator.onLine) {
          errorMessage = 'No network connection!';
        }
        break;
      case 502:
      case 500:
      case 501:
        break;
      case 401:
        if ((await instance.auth.getAccessToken()) !== null) {
          try {
            await startAccessTokenRefreshProcess(true);

            if (!error.config) {
              return;
            }

            // resend the request with same config but new token, if token refresh successful
            const newConfig = {
              ...error.config,
              headers: {
                ...error.config.headers,
                Authorization:
                  'bearer ' + (await instance.auth.getAccessToken()),
              },
            };
            return await instance.client.request(newConfig);
          } catch (err) {
            await instance.config.onLogout();
            errorMessage = 'Your session has expired please login again.';
          }
        } else {
          // AMS mobile (device-auth path): retry up to 3 times with a fresh
          // Hawk header (new nonce + timestamp) on each attempt.
          // The backend only rejects duplicate nonce+timestamp combos,
          // so a fresh header on retry will succeed.
          const retryCount = error.config?._retryCount || 0;
          const maxRetries = 3;

          if (retryCount < maxRetries) {
            const retryConfig = {
              ...error.config,
              _retryCount: retryCount + 1,
            };
            return instance.client.request(retryConfig);
          }

          // All retries exhausted — logout
          console.error('MAX_AUTH_ERROR_THRESHOLD_REACHED');
          await instance.config.onLogout('MAX_AUTH_ERROR_THRESHOLD_REACHED');
          errorMessage = getErrorMessage(
            data,
            errorMessage || 'Your session has expired please login again.'
          );
        }
        break;
      case 403:
        getErrorMessage(
          data,
          errorMessage || 'You do not have permission to perform this action.'
        );
        break;
      case 404:
        errorMessage = 'Requested resource not found.';
        break;
      default:
        errorMessage = getErrorMessage(data, errorMessage);
    }

    const exception = new Error(errorMessage);
    if (data && data.error && data.error.message) {
      // @ts-ignore
      exception.fields = data.error.message;
    }

    if (data?.error?.message?.display_text) {
      // @ts-ignore
      exception.info = data.error.message;
    }
    // @ts-ignore
    exception.response = response;
    return Promise.reject(exception);
  }
);

export const configureSiteVersion = (siteVersion: string) => {
  // @ts-ignore
  instance.client.defaults.headers = {
    ...instance.client.defaults.headers,
    // @ts-ignore
    'x-site-version': siteVersion,
  };
};

export let captureOneSignalRetryCount = 0;
export function setCaptureOneSignalRetryCount(count: number) {
  captureOneSignalRetryCount = count;
}
export const configureOneSignalSubscriptionId = (subscriptionId: string) => {
  // @ts-ignore
  instance.client.defaults.headers = {
    ...instance.client.defaults.headers,
    // @ts-ignore
    'x-onesignal-subscription-id': subscriptionId,
  };
};

export const removeOneSignalSubscriptionId = () => {
  // @ts-ignore
  instance.client.defaults.headers = {
    ...instance.client.defaults.headers,
    // @ts-ignore
    'x-onesignal-subscription-id': undefined,
  };
};

export const configure = ({
  apiBaseURL = '',
  storage = asyncStorage,
  onLogout,
  siteVersion = 'UNKNOWN',
  siteId = 'default',
  deviceIdGenerator,
}: Omit<Config, 'deviceId'>): void => {
  if (isValidURL(apiBaseURL)) {
    instance.client.defaults.baseURL = apiBaseURL;
  }
  if (storage) {
    instance.setStorage(storage);
  }
  // @ts-ignore
  instance.client.defaults.headers = {
    // @ts-ignore
    'x-site-version': siteVersion,
    'x-site-id': siteId,
  };
  instance.config.onLogout = async (): Promise<void> => {
    await instance.auth.removeAccessToken();
    await instance.auth.removeRefreshToken();
    if (typeof onLogout === 'function') {
      await onLogout();
    }
  };
  instance.config.deviceIdGenerator =
    deviceIdGenerator || (async () => 'UNKNOWN');
};

export const configureLogout = ({ onLogout }: Pick<Config, 'onLogout'>) => {
  instance.config.onLogout = async (): Promise<void> => {
    await instance.auth.removeAccessToken();
    await instance.auth.removeRefreshToken();
    if (typeof onLogout === 'function') {
      await onLogout();
    }
  };
};

export const configureDevice = async ({
  apiBaseURL = '',
  storage = asyncStorage,
  siteId,
  siteVersion,
  onLogout,
  deviceId,
}: Pick<
  Config,
  'deviceId' | 'apiBaseURL' | 'storage' | 'onLogout' | 'siteId' | 'siteVersion'
>) => {
  if (isValidURL(apiBaseURL)) {
    instance.client.defaults.baseURL = apiBaseURL;
  }

  if (storage) {
    instance.setStorage(storage);
  }

  // @ts-ignore
  instance.client.defaults.headers = {
    // @ts-ignore
    'x-site-version': siteVersion,
    // @ts-ignore
    'x-site-id': siteId,
    // @ts-ignore
    'x-device-id': typeof deviceId === 'function' ? await deviceId() : deviceId,
  };

  instance.config.onLogout = async (message): Promise<void> => {
    await removeDeviceCredentials();
    await removeDeviceIdFromStorage();
    if (typeof onLogout === 'function') {
      await onLogout(message);
    }
  };
};

export const configureDeviceId = async (deviceId: () => Promise<string>) => {
  // @ts-ignore
  instance.client.defaults.headers = {
    ...instance.client.defaults.headers,
    // @ts-ignore
    'x-device-id': await deviceId(),
  };
};
